import type { AgentError } from "../types/ai_agent.ts";
import type { ChatInterruptMetadata, ChatTool } from "../types/chat.ts";

/**
 * Erros estruturados do chat.
 *
 * O backend nunca manda texto cru de provedor (OpenAI, Bybit…) para o
 * cliente: todo erro chega como `AgentError` (`{ code, message, status }`) em
 * `stream_end.error` ou em `tool_end.output.structured_content` quando
 * `status === "error"`. Aqui só o `code` importa — é a chave de tradução.
 * `message` é um fallback em inglês para código desconhecido.
 */

export const INTERNAL_AGENT_ERROR_CODE = "E_INTERNAL_SERVER_ERROR";

const GENERIC_MESSAGE = "Algo deu errado. Tente novamente.";

/**
 * Tradução por `code`. Não é uma lista fechada: código desconhecido cai no
 * genérico. Os códigos de domínio da API (trade, PIX, saque) são os mesmos
 * que a REST devolve, então dá para reaproveitar as strings das telas.
 */
const AGENT_ERROR_MESSAGES: Record<string, string> = {
  // Erros de LLM / grafo (chegam em `stream_end.error`)
  E_LLM_RATE_LIMITED:
    "Muitas requisições ao assistente agora. Aguarde alguns segundos e tente de novo.",
  E_LLM_QUOTA_EXHAUSTED:
    "O assistente está temporariamente indisponível. Já fomos avisados.",
  E_LLM_UNAVAILABLE:
    "Não conseguimos falar com o assistente. Tente de novo em instantes.",
  E_INTERNAL_SERVER_ERROR: GENERIC_MESSAGE,

  // Erros de tool (chegam em `tool_end.output.structured_content`)
  E_TOOL_EXECUTION_FAILED: "A ferramenta falhou ao executar.",
  E_QUOTE_NOT_FOUND: "Cotação não encontrada para este par.",
  E_MCP_AUTHENTICATION_REQUIRED: "Sessão do assistente expirou. Recarregue a página.",
  E_NOT_FOUND_ERROR: "Recurso não encontrado.",
  E_BAD_REQUEST_ERROR: "Pedido inválido.",
  E_UNPROCESSABLE_ERROR: "Não foi possível processar o pedido.",
  E_UNAUTHORIZED_ERROR: "Sessão expirada. Faça login novamente.",
  E_FORBIDDEN_ERROR: "Você não tem permissão para esta ação.",
  E_TOO_MANY_REQUESTS_ERROR: "Muitas tentativas. Aguarde um pouco.",
  E_SERVICE_UNAVAILABLE: "Serviço indisponível no momento.",

  // Domínio (mesmos códigos da API REST)
  E_INSUFFICIENT_AVAILABLE_BALANCE: "Saldo disponível insuficiente.",
  E_ORDER_AMOUNT_TOO_SMALL: "Valor da ordem abaixo do mínimo.",
  E_ORDER_QTY_TOO_SMALL: "Quantidade da ordem abaixo do mínimo.",
  E_QTY_ABOVE_MAX: "Quantidade acima do máximo permitido.",
  E_PAIR_NOT_FOUND: "Par não encontrado.",
  E_LINEAR_PAIR_NOT_FOUND: "Par de futuros não encontrado.",
  E_NO_OPEN_POSITION: "Não há posição aberta para fechar.",
  E_OPPOSITE_POSITION_OPEN: "Há uma posição aberta no sentido oposto.",
  E_INVALID_LEVERAGE: "Alavancagem inválida.",
  E_LEVERAGE_OUT_OF_RANGE: "Alavancagem fora do intervalo permitido.",
  E_INVALID_TPSL: "Take profit / stop loss inválidos.",
  E_BYBIT_SUBACCOUNT_MISSING: "Sua conta de trading ainda não foi criada.",
  E_BENEFICIARY_REQUIRED: "É preciso cadastrar um beneficiário antes.",
  E_POOL_TRANSFER_INSUFFICIENT_BALANCE: "Saldo insuficiente para o saque.",
  E_POOL_TRANSFER_BELOW_MIN: "Valor do saque abaixo do mínimo.",
  E_POOL_TRANSFER_ABOVE_MAX: "Valor do saque acima do máximo.",
  E_POOL_TRANSFER_ADDRESS_NOT_WHITELISTED: "Endereço não está na lista permitida.",
  E_POOL_TRANSFER_NETWORK_UNAVAILABLE: "Rede indisponível para saque agora.",
};

/** Erros de LLM em que reenviar a mensagem tem chance de funcionar. */
const RETRYABLE_CODES = new Set(["E_LLM_RATE_LIMITED", "E_LLM_UNAVAILABLE"]);

export function isAgentError(value: unknown): value is AgentError {
  if (!value || typeof value !== "object") return false;

  const record = value as Record<string, unknown>;

  return typeof record.code === "string" && record.code.length > 0;
}

/**
 * Garante a forma `AgentError` para qualquer coisa que venha no lugar de um
 * erro: objeto já estruturado, string (backend antigo em replay, ou erro de
 * transporte do fetch) ou nada. Sem `code` reconhecível → erro interno.
 */
export function normalizeAgentError(raw: unknown): AgentError {
  if (isAgentError(raw)) {
    return {
      code: raw.code,
      message: typeof raw.message === "string" ? raw.message : "",
      status: typeof raw.status === "number" ? raw.status : 500,
    };
  }

  return {
    code: INTERNAL_AGENT_ERROR_CODE,
    message: typeof raw === "string" && raw.length > 0 ? raw : "Something went wrong",
    status: 500,
  };
}

/** Texto para o usuário. Só o `code` decide; `message` nunca é exibida. */
export function agentErrorText(error: AgentError): string {
  return AGENT_ERROR_MESSAGES[error.code] ?? GENERIC_MESSAGE;
}

export function isRetryableAgentError(error: AgentError): boolean {
  return RETRYABLE_CODES.has(error.code);
}

/**
 * Erro estruturado de um `tool_end` com `status: "error"`. Lê
 * `output.structured_content`; nunca `output.content`, que é o texto que o
 * modelo leu e pode carregar mensagem crua.
 */
export function toolErrorFromToolEnd(data: unknown): AgentError | null {
  if (!data || typeof data !== "object") return null;

  const output = (data as Record<string, unknown>).output;

  if (!output || typeof output !== "object") return null;

  const structured = (output as Record<string, unknown>).structured_content;

  return isAgentError(structured) ? normalizeAgentError(structured) : null;
}

/** Texto do estado de erro do card de tool. */
export function toolErrorText(tool: ChatTool): string {
  const error = toolErrorFromToolEnd(tool.tool_end);

  return error ? agentErrorText(error) : GENERIC_MESSAGE;
}

/**
 * Desfecho de falha para gravar no interrupt a partir de um `tool_end`.
 * `code` vem do `structured_content`; `reason` é o fallback em string para
 * eventos fora do contrato (histórico antigo).
 */
export function toolFailureFromToolEnd(
  data: unknown,
): { code: string | null; reason: string | null } {
  const error = toolErrorFromToolEnd(data);

  if (error) {
    return { code: error.code, reason: error.message || null };
  }

  if (!data || typeof data !== "object") return { code: null, reason: null };

  const output = (data as Record<string, unknown>).output;

  if (typeof output === "string") return { code: null, reason: output };

  if (output && typeof output === "object") {
    const record = output as Record<string, unknown>;

    for (const key of ["content", "message", "error"]) {
      const value = record[key];

      if (typeof value === "string" && value.length > 0) {
        return { code: null, reason: value };
      }
    }
  }

  return { code: null, reason: null };
}

/**
 * Texto de falha para os cards HITL ("aprovado, mas falhou"). Prefere o
 * `failureCode` traduzido; `failureReason` só entra quando não há código
 * (interrupts persistidos antes do contrato estruturado).
 */
export function interruptFailureText(
  metadata: ChatInterruptMetadata | undefined,
): string | null {
  const code = metadata?.failureCode;

  if (typeof code === "string" && code.length > 0) {
    return AGENT_ERROR_MESSAGES[code] ?? GENERIC_MESSAGE;
  }

  const reason = metadata?.failureReason;

  return typeof reason === "string" && reason.length > 0 ? reason : null;
}
